import { Box, CircularProgress, DialogContent, Stack } from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { OpenShiftCardContainer } from "@src/appV2/redesign/Shift/Open/CardContainer";
import { type RateNegotiation } from "@src/appV2/redesign/Shift/Open/types";
import { OpenShiftListDataProvider } from "@src/appV2/redesign/Shift/Open/useOpenShiftListDataContext";
import { SHIFT_DISCOVERY_SHIFT_MODAL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useShiftModalsDataContext";
import { CbhIcon, Text, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useMemo } from "react";

import { Button } from "../../components/Button";
import { useGetFcfReplacementShifts } from "./hooks/useGetFcfReplacementShifts";
import { type FcfDeepLinkParams } from "./types";

const EMPTY_RATE_NEGOTIATIONS_MAP = new Map<string, RateNegotiation>();

interface FcfReplacementShiftsBottomSheetProps {
  modalState: UseModalState;
  onDismiss: () => void;
  params: FcfDeepLinkParams;
}

export function FcfReplacementShiftsBottomSheet(props: FcfReplacementShiftsBottomSheetProps) {
  const { modalState, onDismiss, params } = props;
  const { navigateToModal } = useShiftModalsDataContext();

  const { replacementShifts, isLoading } = useGetFcfReplacementShifts({
    ...params,
    enabled: modalState.modalIsOpen,
  });

  const isEmpty = !isLoading && replacementShifts.length === 0;

  const workplacesMap = useMemo(
    () => new Map(replacementShifts.map(({ workplace }) => [workplace.id, workplace])),
    [replacementShifts]
  );

  function handleShiftTap(shiftId: string): void {
    navigateToModal(SHIFT_DISCOVERY_SHIFT_MODAL_PATH, {
      clearExistingSearchParams: true,
      pathParams: { shiftId },
    });
  }

  return (
    <FullScreenDialog modalState={modalState} variant="primary">
      <Box sx={{ display: "flex", justifyContent: "flex-end", paddingX: 5 }}>
        <IconButton
          iconType="close"
          aria-label="Close"
          variant="outlined"
          size="medium"
          onClick={onDismiss}
        />
      </Box>
      <Stack spacing={2} sx={{ paddingX: 4 }}>
        <Text
          textAlign="center"
          variant="body2"
          sx={{ color: (theme) => theme.palette.text.secondary }}
        >
          We're sorry your shift was cancelled
        </Text>
        <Text
          textAlign="center"
          variant="h4"
          sx={{ color: (theme) => theme.palette.text?.primary }}
        >
          Here are some nearby shifts starting at the same time
        </Text>
      </Stack>
      <DialogContent>
        {isLoading && (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              justifyContent: "center",
              paddingY: 10,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {isEmpty && (
          <Stack alignItems="center" spacing={4} sx={{ paddingY: 10 }}>
            <CbhIcon size="large" type="search" />
            <Text
              textAlign="center"
              variant="body1"
              sx={{ color: (theme) => theme.palette.text?.secondary ?? "" }}
            >
              No similar shifts are available right now.
            </Text>
          </Stack>
        )}

        {!isLoading && !isEmpty && (
          <OpenShiftListDataProvider
            openShifts={replacementShifts.map(({ shift }) => shift)}
            workplacesMap={workplacesMap}
            rateNegotiationsMap={EMPTY_RATE_NEGOTIATIONS_MAP}
          >
            <Stack spacing={3}>
              {replacementShifts.map(({ shift, workplace }) => (
                <OpenShiftCardContainer
                  key={shift.id}
                  shift={shift}
                  workplace={workplace}
                  workers={[]}
                  onClick={() => {
                    handleShiftTap(shift.id);
                  }}
                />
              ))}
            </Stack>
          </OpenShiftListDataProvider>
        )}
      </DialogContent>

      <DialogFooter orientation="horizontal">
        <Button fullWidth size="large" variant="contained" onClick={onDismiss}>
          I'm not interested
        </Button>
      </DialogFooter>
    </FullScreenDialog>
  );
}
